import { useEffect, useRef, useState } from 'react';

import { ComparisonSliderData } from '../../../Content';
import { useDimensions } from '../../../hooks/useDimensions';
import { useDrag } from 'react-use-gesture';
import { useScene } from '../../../contexts/SceneContext';

interface Props {
  content: ComparisonSliderData;
}

const HANDLE_SIZE = 40;
const HANDLE_PADDING = 10;
const HANDLE_CONTAINER_SIZE = 44;
const HANDLE_BORDER_THICKNESS = 4;
const HANDLE_OVERALL_SIZE = HANDLE_CONTAINER_SIZE + HANDLE_BORDER_THICKNESS;

export const ComparisonSliderContent: React.FC<Props> = ({ content }) => {
  const { parseContentPath } = useScene();

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useDimensions(containerRef);
  const [lastContainerSize, setLastContainerSize] = useState(containerSize);

  const [x, setX] = useState(0);

  const bind = useDrag(({ xy: [x] }) => {
    const clientX = containerRef.current?.getBoundingClientRect().x || 0;
    setX(
      Math.min(
        containerSize.w - HANDLE_CONTAINER_SIZE / 2 - HANDLE_PADDING,
        Math.max(HANDLE_OVERALL_SIZE / 2 + HANDLE_PADDING, x - clientX)
      )
    );
  });

  useEffect(() => {
    const ratio =
      lastContainerSize.w === 0 ? 0 : containerSize.w / lastContainerSize.w;
    setX((x) =>
      Math.min(
        containerSize.w - HANDLE_CONTAINER_SIZE / 2 - HANDLE_PADDING,
        Math.max(HANDLE_OVERALL_SIZE / 2 + HANDLE_PADDING, x * ratio)
      )
    );
    setLastContainerSize(containerSize);
  }, [containerSize, lastContainerSize.w]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <img
        src={parseContentPath(content.afterSrc)}
        alt="after"
        style={{
          maxWidth: '100%',
          display: 'block',
          pointerEvents: 'none',
          width: '100%',
        }}
      />
      <span
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          color: 'white',
          padding: '1em',
        }}
      >
        After
      </span>
      <div
        style={{
          width: `${(x / containerSize.w) * 100}%`,
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'hidden',
        }}
      >
        <img
          src={parseContentPath(content.beforeSrc)}
          alt="before"
          style={{
            height: '100%',
            maxWidth: 'none',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            color: 'white',
            padding: '1em',
          }}
        >
          Before
        </span>
      </div>
      <div
        style={{
          width: 0,
          height: 0,
          padding: `${HANDLE_CONTAINER_SIZE / 2}px`,
          margin: `-${
            HANDLE_CONTAINER_SIZE / 2 + HANDLE_BORDER_THICKNESS
          }px 0 0 -${HANDLE_CONTAINER_SIZE / 2 + HANDLE_BORDER_THICKNESS}px`,
          position: 'absolute',
          top: '50%',
          left: `${(x / containerSize.w) * 100}%`,
          borderRadius: '50%',
          border: `${HANDLE_BORDER_THICKNESS}px solid #0000006e`,
        }}
      >
        <div
          {...bind()}
          style={{
            width: `${HANDLE_SIZE}px`,
            height: `${HANDLE_SIZE}px`,
            margin: `-${HANDLE_SIZE / 2}px 0 0 -${HANDLE_SIZE / 2}px`,
            backgroundImage: 'url("assets/slider_icon.png")',
            backgroundSize: 'contain',
            touchAction: 'none',
          }}
        />
      </div>
    </div>
  );
};
